import React, { createContext, useContext, useState, ReactNode } from 'react';
import ReactDOM from 'react-dom';
import ToastNotification from '../components/ToastNotification/ToastNotification';
import '../components/ToastNotification/toast.scss';
import { NOTIFICATION_TYPES } from '../components/ToastNotification/toastConstants';

interface Toast {
  id: number;
  type:  NOTIFICATION_TYPES;
  title: string;
  description?: string;
  view?: boolean;
  persistent?: boolean;
}

interface ToastContextType {
  addToast: (type:  NOTIFICATION_TYPES, title: string, description?: string, view?: boolean, persistent?: boolean) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const durationTime = 7000;

  const addToast = (type: NOTIFICATION_TYPES, title: string, description?: string, view?: boolean, persistent?: boolean) => {
    const id = Date.now();
    setToasts((prevToasts) => [...prevToasts, { id, type, title, description: description || '', view: view || false, persistent }]);
  };

  const removeToast = (id: number) => {
    setToasts((currentToasts) => currentToasts.filter(toast => toast.id !== id));
  };

  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      {ReactDOM.createPortal(
        <div className="toast-container">
          {toasts.map((toast, index) => (
            <ToastNotification
              key={toast.id}
              type={toast.type}
              title={toast.title}
              description={toast.description}
              view={toast.view}
              onClose={() => removeToast(toast.id)}
              duration={toast.persistent ? undefined : durationTime + index * 10}
              persistent={toast.persistent}
            />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};